import {
  SortDirection,
  Table,
  TableCell,
  TableHeader,
} from '@toggle/design-system';
import { getCurrencySymbol } from '@toggle/helpers/src/utils/currency/currency';
import orderBy from 'lodash/orderBy';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';

import { History, HistoryResults } from '@/api/asset/asset-schema';
import { useTouchDevice } from '@/stores/MediaQueryStore';
import { createLogGA } from '@/utils/analytics/logGA';

import {
  CellValueType,
  ExpectationCell,
} from '../expectation-cell/ExpectationCell';
import {
  gaTableHeaderMapping,
  getHeadingsData,
  TableHeaderEnum,
} from './past-earning-table-helpers';
import { PastEpisodesRow } from './past-episodes-row/PastEpisodesRow';
import * as S from './PastEarningsTable.styles';

interface PastEarningTableProps {
  isScrollbarVisible: boolean;
  history: History;
  currency: string;
  reported: boolean;
}

export const PastEarningsTable = ({
  isScrollbarVisible,
  history,
  currency,
  reported,
}: PastEarningTableProps) => {
  const { t } = useTranslation('common');
  const isTouchDevice = useTouchDevice();
  const currencySymbol = getCurrencySymbol(currency);
  const headingsData = getHeadingsData(t, currencySymbol);
  const [activeHeader, setActiveHeader] = useState(
    TableHeaderEnum.ReportingDate
  );

  const sortDateData = (
    sort: SortDirection,
    history: History
  ): HistoryResults[] => {
    return orderBy(
      history.results,
      e => new Date(e.reporting_date),
      sort === SortDirection.asc ? 'asc' : 'desc'
    );
  };

  const [filteredData, setFilteredData] = useState<HistoryResults[]>(() =>
    sortDateData(SortDirection.dsc, history)
  );

  const handleSort = (sort: SortDirection, selectedHeader: TableHeaderEnum) => {
    const dataCopy = history;
    if (
      selectedHeader !== TableHeaderEnum.FiscalPeriod &&
      selectedHeader !== TableHeaderEnum.ReportingDate &&
      selectedHeader !== TableHeaderEnum.Count
    ) {
      const sortedData = sortNumberData(sort, selectedHeader, dataCopy);
      setFilteredData([...sortedData]);
    } else {
      const sortedDates = sortDateData(sort, dataCopy);
      setFilteredData([...sortedDates]);
    }

    setActiveHeader(selectedHeader);
    createLogGA('Sort')('click_sorting', {
      sortBy: gaTableHeaderMapping[selectedHeader],
      releaseStatus: reported ? 'Released' : 'Upcoming',
    });
  };

  const sortNumberData = (
    sort: SortDirection,
    selectedHeader: Exclude<
      TableHeaderEnum,
      'reporting_date' | 'fiscal_period' | 'count'
    >,
    history: History
  ): HistoryResults[] => {
    return history.results.sort((a, b) => {
      const firstValue = a[selectedHeader];
      const nextValue = b[selectedHeader];
      if (!firstValue) {
        return 1;
      } else if (!nextValue) {
        return -1;
      } else {
        return sort === SortDirection.asc
          ? firstValue - nextValue
          : nextValue - firstValue;
      }
    });
  };

  const getYearDisplay = (year: number) => {
    if (isTouchDevice) {
      return `’${year.toString().slice(-2)}`;
    } else {
      return year.toString();
    }
  };

  return (
    <Table
      TableHeaderContent={
        <>
          {!isTouchDevice && (
            <S.ExtraTableHeader>
              <TableCell empty condensed />
              <TableCell empty condensed />
              <TableCell colspan={3} alignment="center" isNumber={false}>
                {t('table.moveAfterEarnings')}
              </TableCell>
            </S.ExtraTableHeader>
          )}
          <S.TableHeaderRow>
            {headingsData.map((d, i) => (
              <TableHeader
                key={i}
                alignment={d.alignment}
                isSortable={d.type !== TableHeaderEnum.Count}
                isSorted={d.type === activeHeader}
                headerClick={sortDirection => handleSort(sortDirection, d.type)}
                fixed={
                  d.type === TableHeaderEnum.ReportingDate && isScrollbarVisible
                }
              >
                {d.name}
              </TableHeader>
            ))}
          </S.TableHeaderRow>
        </>
      }
      TableBodyContent={
        <>
          {!isTouchDevice && <PastEpisodesRow meta={history.meta} />}
          {filteredData.map((d, i) => (
            <tr key={i}>
              <TableCell
                data-testid={`period-${i}`}
                alignment="start"
                fixed={isScrollbarVisible}
              >{`${d.fiscal_period} ${getYearDisplay(
                d.fiscal_year
              )}`}</TableCell>
              <ExpectationCell
                data-testid={`value-${i}`}
                value={d.eps_surprise === null ? '?' : d.eps_surprise}
              />
              <ExpectationCell
                value={d.w1 ?? '-'}
                typeOfValue={CellValueType.Percentage}
              />
              <ExpectationCell
                value={d.w2 ?? '-'}
                typeOfValue={CellValueType.Percentage}
              />
              <ExpectationCell
                value={d.m1 ?? '-'}
                typeOfValue={CellValueType.Percentage}
              />
            </tr>
          ))}
        </>
      }
    />
  );
};
